import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { LightDomController } from '../common/controllers/LightDomController.js'
import { LightSlotController } from '../common/controllers/LightSlotController.js'
import { observe } from '../common/decorators/observe.js'
import { cond } from '../common/directives/cond.js'
import { NordEvent } from '../common/events.js'

import { FocusableMixin } from '../common/mixins/FocusableMixin.js'
import { InputMixin } from '../common/mixins/InputMixin.js'

import componentStyle from '../common/styles/Component.css'
import style from './SegmentedControlItem.css'

let id = 0
const createId = (suffix: string) => `nord-segmented-control-item-${suffix}-${id++}`

function isLabel(element: Element): element is HTMLLabelElement {
  return element.localName === 'label'
}

type NativeInputElement = HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement

/**
 * Segmented control items populate a segmented control with options.
 * Every item should be placed inside a segmented control.
 *
 * @status new
 * @category action
 * @slot label - Use when a label requires more than plain text.
 *
 * @cssprop [--n-segmented-control-item-border-radius=var(--n-border-radius-s)] - Controls the rounded corners of the item, using [border radius tokens](/tokens/#border-radius).
 * @cssprop [--n-segmented-control-item-gap=var(--n-space-xs)] - Controls the spacing between elements within the item, using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-segmented-control-item-gradient=linear-gradient(to bottom, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.013) 100%))] - Controls the overlayed gradient background on the item.
 * @cssprop [--n-segmented-control-item-background-color=var(--n-color-nav-hover)] - Controls the background color of the item, using our [color tokens](/tokens/#color).
 * @cssprop [--n-segmented-control-item-color=var(--n-color-text-weaker)] - Controls the color of the text within the item, using our [color tokens](/tokens/#color).
 * @cssprop [--n-segmented-control-item-padding-inline=calc(var(--n-space-m) / 1.2)] - Controls the inline, or left and right, padding of the item.
 * @cssprop [--n-segmented-control-item-box-shadow=none] - Controls the surrounding shadow, using our [box shadow tokens](/tokens/#box-shadow).
 * @cssprop [--n-segmented-control-item-font-size=var(--n-font-size-m)] - Controls the size of the text within the item, using our [font tokens](/tokens/#font).
 * @cssprop [--n-segmented-control-item-font-weight=var(--n-font-weight)] - Controls the weight of the text within the item, using our [font tokens](/tokens/#font).
 * @cssprop [--n-segmented-control-item-min-block-size=calc(var(--n-space-xl) - 2px)] - Controls the minimum block size, or height, of the item using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-segmented-control-item-inline-size=auto] - Controls the inline size of the item.
 *
 * @parent SegmentedControl
 */
@customElement('nord-segmented-control-item')
export default class SegmentedControlItem extends InputMixin(FocusableMixin(LitElement)) {
  static styles = [componentStyle, style]

  protected inputId = createId('input')

  /**
   * For accessibility reasons, we render the form parts of the component to the light DOM.
   */
  protected inputSlot = new LightDomController(this, {
    render: () => html`
      <input
        slot="input"
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
        ${ref(this.focusableRef)}
        id=${this.inputId}
        type="radio"
        class="n-input"
        name=${cond(this.name)}
        .value=${cond(this.value)}
        .checked=${this.checked}
        ?disabled=${this.disabled}
        form=${cond(this._formId)}
      />
    `,
  })

  protected labelSlot = new LightSlotController(this, {
    slotName: 'label',
    render: () =>
      this.label
        ? html`<label slot="label-internal" for=${this.inputId} class="n-segmented-control-item">${this.label}</label>`
        : nothing,
    syncLightDom: (element) => {
      if (!isLabel(element)) {
        console.warn(`NORD: Only <label> elements should be placed in Segmented Control's "label" slot`)
      }
      else {
        element.htmlFor = this.inputId
      }
    },
  })

  /**
   * Controls whether the segmented control item is checked or not.
   */
  @property({ type: Boolean, reflect: true }) checked: boolean = false

  /**
   * The size of the segmented control item.
   * This affects font-size and padding.
   */
  @property({ reflect: true }) size: 's' | 'm' | 'l' = 'm'

  /**
   * Label for the segmented control item.
   */
  @property({ reflect: true }) label: string = ''

  render() {
    return html`<div class="n-segmented-control-item" @change=${this.handleChange}>
      <slot name="input"></slot>
      <slot name="label"></slot>
      <slot name="label-internal"></slot>
    </div>`
  }

  @observe('checked')
  protected handleCheckedChange(previousChecked: boolean) {
    // if this component was previous unchecked but is now checked,
    // then we need to uncheck any items in the same group
    if (!previousChecked && this.checked) {
      this.uncheckSiblings()
    }
  }

  private uncheckSiblings() {
    const root = this.getRootNode() as Document | ShadowRoot

    root
      .querySelectorAll<SegmentedControlItem>(`nord-segmented-control-item[name="${this.name}"]`)
      .forEach((segmentedControlItem) => {
        if (segmentedControlItem !== this) {
          segmentedControlItem.checked = false
        }
      })
  }

  protected handleChange(e: Event): void {
    const target = e.target as HTMLInputElement
    this.checked = target.checked

    e.stopPropagation()

    /**
     * Fired whenever the segmented control item has been checked.
     */
    this.dispatchEvent(new NordEvent('change'))
  }

  private handleBlur = (e: Event) => {
    e.stopPropagation()
    this.dispatchEvent(new Event('blur', { bubbles: false, cancelable: true }))
  }

  private handleFocus = (e: Event) => {
    e.stopPropagation()
    this.dispatchEvent(new Event('focus', { bubbles: false, cancelable: true }))
  }

  protected handleInput(e: Event) {
    e.stopPropagation()
    const target = e.target as NativeInputElement
    this.value = target.value

    /**
     * Fired as the user types into the input.
     */
    this.dispatchEvent(new NordEvent('input'))
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-segmented-control-item': SegmentedControlItem
  }
}
